import { useCallback, useState } from 'react'

import { Button, Heading, SkeletonBox, Statement } from '@circlefin/components'
import { useForm } from '@circlefin/form'
import { LocalsForms } from '@features/locales/forms'
import { useCountries } from '@features/locales/hooks/countries'
import {
  getError,
  BILLING_PROFILE_NOT_FOUND,
} from '@shared/apollo/lib/error/codes'
import { TransWithLink } from '@shared/components/common'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  LegalEntity,
  useBillingProfileQuery,
  useUpdateBillingProfileMutation,
} from '@shared/graphql'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'

import {
  billingProfileFormSchema,
  vatNumberSchema,
} from './BillingProfile.schema'

import type { BillingProfileFormValues } from './BillingProfile.schema'
import type { CountryCode } from '@shared/graphql'

export const BillingProfile: React.FC = () => {
  const { t } = useTranslation('settings')

  const [isEditing, setIsEditing] = useState(false)

  const { data } = useSession()

  const isEeaEntity = data?.legalEntity === LegalEntity.CIRCLE_FR

  const {
    data: billingProfileData,
    loading: billingProfileLoading,
    error: billingProfileError,
    refetch: billingProfileRefetch,
  } = useBillingProfileQuery()

  const [
    updateBillingProfile,
    {
      loading: updateProfileLoading,
      error: updateProfileError,
      reset: updateProfileReset,
    },
  ] = useUpdateBillingProfileMutation({
    onError: () => {},
  })

  const billingProfile = billingProfileData?.billingProfile ?? {}

  const [Form, { watch, reset }] = useForm<BillingProfileFormValues>({
    schema: billingProfileFormSchema.shape({
      vatNumber: vatNumberSchema.when([], {
        // Require vatNumber only for EEA countries
        is: () => isEeaEntity,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    mode: 'onTouched',
    values: {
      billingContact: {
        email: billingProfile.billingContact?.email ?? '',
      },
      billingAddress: {
        line1: billingProfile.billingAddress?.line1 ?? '',
        line2: billingProfile.billingAddress?.line2 ?? '',
        city: billingProfile.billingAddress?.city ?? '',
        district: billingProfile.billingAddress?.district ?? '',
        postalCode: billingProfile.billingAddress?.postalCode ?? '',
        country:
          (billingProfile.billingAddress?.country as CountryCode) ?? undefined,
      },
      phoneNumber: billingProfile.phoneNumber ?? '',
      vatNumber: billingProfile.vatNumber ?? '',
    },
  })

  const { loading: countriesLoading, countries } = useCountries({
    blockchain: {
      registeredIn: true,
    },
    onboarding: {
      sanctioned: false,
    },
  })

  const handleEdit = useCallback(() => {
    setIsEditing(true)
  }, [])

  const handleCancel = useCallback(() => {
    // If cancelling, reset any changes made to the form
    reset()
    setIsEditing(false)
  }, [reset])

  const handleSubmit = useCallback(
    async (values: BillingProfileFormValues) => {
      const { billingAddress } = values
      // Omit country field from update request
      const { country, ...rest } = billingAddress

      await updateBillingProfile({
        variables: {
          input: {
            ...values,
            billingAddress: {
              ...rest,
            },
          },
        },
        onCompleted: () => {
          setIsEditing(false)
        },
      })
    },
    [updateBillingProfile],
  )

  // If billing profile not found, display specific error message
  if (
    billingProfileError &&
    getError(BILLING_PROFILE_NOT_FOUND, billingProfileError.graphQLErrors)
  ) {
    return (
      <Statement
        status="warning"
        subtitle={
          <TransWithLink
            i18nKey="common:graphQL.error.BILLING_PROFILE_NOT_FOUND"
            variant="support"
            noWrap
          />
        }
      />
    )
  }

  return (
    <GraphQLErrorBoundary
      error={billingProfileError}
      retry={billingProfileRefetch}
    >
      <GraphQLErrorBoundary
        error={updateProfileError}
        retry={updateProfileReset}
      >
        <Form className="flex w-136 flex-col gap-4" onSubmit={handleSubmit}>
          <Heading variant="title/sm">
            <Heading.Header>{t`billing.profile.heading`}</Heading.Header>
            <Heading.Intro>{t`billing.profile.subheading`}</Heading.Intro>
          </Heading>

          <SkeletonBox
            className="mt-6 h-10 w-full"
            loading={countriesLoading || billingProfileLoading}
          >
            <Form.Dropdown
              data-testid="billing-profile-country"
              items={countries.toDropdown()}
              label={t`billing.profile.form.billingAddress.country`}
              name="billingAddress.country"
              disabled
            />
          </SkeletonBox>

          <Form.Input
            className="w-full"
            disabled={!isEditing}
            label={t`billing.profile.form.billingAddress.line1`}
            loading={billingProfileLoading}
            name="billingAddress.line1"
          />

          <LocalsForms.Input.LineTwo
            disabled={!isEditing}
            label={t`billing.profile.form.billingAddress.line2`}
            name="billingAddress.line2"
          />

          <div className="grid grid-cols-3 gap-2">
            <Form.Input
              disabled={!isEditing}
              label={t`billing.profile.form.billingAddress.city`}
              loading={billingProfileLoading}
              name="billingAddress.city"
            />

            <LocalsForms.Dropdown.State
              country={watch('billingAddress.country') as CountryCode}
              disabled={!isEditing}
              loading={billingProfileLoading}
              name="billingAddress.district"
            />

            <Form.Input
              disabled={!isEditing}
              label={t`billing.profile.form.billingAddress.postalCode`}
              loading={billingProfileLoading}
              name="billingAddress.postalCode"
            />
          </div>

          <Form.Input
            disabled={!isEditing}
            label={t`billing.profile.form.phoneNumber`}
            loading={billingProfileLoading}
            name="phoneNumber"
          />

          <Form.Input
            disabled={!isEditing}
            label={t`billing.profile.form.billingContact.email`}
            loading={billingProfileLoading}
            name="billingContact.email"
          />

          {isEeaEntity && (
            <Form.Input
              disabled={!isEditing}
              label={t`billing.profile.form.vatNumber`}
              loading={billingProfileLoading}
              name="vatNumber"
            />
          )}

          {isEditing && (
            <div className="mt-3 flex flex-row gap-x-4">
              <Button
                className="w-fit"
                onClick={handleCancel}
                variant="secondary"
              >{t`common:cancel`}</Button>

              <Form.SubmitButton
                className="w-fit"
                loading={updateProfileLoading}
                variant="primary"
              >
                {t`common:save`}
              </Form.SubmitButton>
            </div>
          )}

          {!isEditing && (
            <Button
              className="mt-3 w-fit"
              onClick={handleEdit}
              variant="primary"
            >{t`common:edit`}</Button>
          )}
        </Form>
      </GraphQLErrorBoundary>
    </GraphQLErrorBoundary>
  )
}
